import { LitElement, css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type {
  AssistantMessage,
  AttachedFile,
  ContractsSlice,
  Operator,
  PendingMessage,
  ServerMessage,
} from '../state/types';
import type { ContractDetailed } from '../api/contracts';
import { iconAttach, iconCheck, iconCheckDouble, iconInfo } from './icons';
import { i18n } from '../i18n/nl';
import { formatUnitAddress } from '../utils/contract-format';
import {
  parseAavaMessage,
  youtubeThumbnailUrl,
  youtubeWatchUrl,
} from '../utils/aava-message';
import './rating-card';
import './self-fix-prompt';
import type { RatingUiVariant } from '../state/selectors';

/**
 * Discriminated union van rendered rows. Module-level zodat zowel
 * buildRows() als renderRow() er typesafe op kunnen narrowen — geen
 * `as unknown`-casts meer nodig om door de structuur te navigeren.
 */
type RowAuthor = 'customer' | 'operator' | 'assistant';
type MessageStatus = 'sending' | 'awaiting' | 'failed' | 'sent';

type Row =
  | { kind: 'day'; id: string; label: string }
  | { kind: 'system'; id: string; text: string; timestamp: number }
  | {
      kind: 'msg';
      id: string;
      author: RowAuthor;
      text: string;
      timestamp: number;
      files?: AttachedFile[];
      status?: MessageStatus;
      showAvatar: boolean;
    };

/**
 * <embrace-message-list> — rendert de volledige message-stream:
 *   - Day dividers ("Vandaag · 24 april")
 *   - System messages ("Je gesprek is gestart met Sanne")
 *   - Server messages + pending messages (optimistic UI)
 *   - Avatar-grouping (avatar alleen bij 1e bericht per auteur-reeks)
 *   - Typing indicator (3 stippen) als agentTyping=true
 *   - Read receipts op eigen berichten (✓ / ✓✓)
 */
@customElement('embrace-message-list')
export class EmbraceMessageList extends LitElement {
  @property({ attribute: false }) serverMessages: ServerMessage[] = [];
  @property({ attribute: false }) pendingMessages: PendingMessage[] = [];
  @property({ attribute: false }) assistantMessages: AssistantMessage[] = [];
  @property({ attribute: false }) operator: Operator | null = null;
  @property({ type: Boolean }) agentTyping = false;
  @property({ type: Boolean }) sessionEnded = false;
  @property({ type: Number }) sessionEndedAt = 0;
  /**
   * Wie heeft het gesprek beëindigd: 'customer' = de bezoeker zelf,
   * 'server' = operator/server. Bepaalt de tekst van de ended-pill.
   */
  @property({ type: String }) sessionEndedReason: 'customer' | 'server' | null = null;

  /**
   * Rating-prompt na ended session. 'hidden' = niet tonen, 'idle' =
   * actieve sterren-form, 'submitted' = bedank-balloon. We renderen hem
   * in-stream (alsof de bot 'm verstuurd) zodat het past in de
   * chat-thread i.p.v. een losse banner buiten de stream.
   */
  @property({ type: String }) ratingVariant: RatingUiVariant = 'hidden';

  /** Voor variant='submitted': de waarde voor de bedank-tekst. */
  @property({ type: Number }) submittedRating = 0;

  /**
   * Self-fix ja/nee-vraag na een in-chat opgeloste AAVA-conversatie
   * (`outcome: Resolved`). In-stream gerenderd als bot-balloon — zelfde
   * behandeling als de rating-prompt — zodat de vraag deel uitmaakt van
   * de thread. Sluit op `aava.selfFix === 'asking'` in panel-views.
   */
  @property({ type: Boolean }) showSelfFix = false;

  /**
   * Inline contract-choices voor de AVAA-flow. Alleen rendered wanneer
   * `showContractChoices = true` (sluit op `assistant.step === 'aava-
   * selecting-contract'` in panel-views). De slice levert loading-/
   * loaded-/error-state zodat we direct kunnen tonen wat van toepassing
   * is — een nog-fetchende lijst krijgt een skeleton, een lege lijst de
   * empty-state copy.
   */
  @property({ type: Boolean }) showContractChoices = false;
  @property({ attribute: false }) contracts: ContractsSlice | null = null;

  /**
   * `true` zolang AAVA een call onderweg heeft (`/start` of `/message`).
   * Toont een bot-typing-indicator onderaan de stream — apart van de
   * 800ms-message-reveal-animatie (`botIsTyping` uit buildRows), die
   * alleen rond een net-toegevoegde assistant-message verschijnt.
   */
  @property({ type: Boolean }) aavaThinking = false;

  /**
   * Volledige AI-gebruiksvoorwaarden (org-naam al geïnterpoleerd). Leeg =
   * niet tonen. Wanneer gezet, rendert bovenaan de stream een inklapbaar
   * `<details>`-blok dat de bezoeker kan openen om alles te lezen.
   */
  @property({ type: String }) aavaDisclaimer = '';

  /**
   * Typing-effect voor de digitale assistent: nieuwe bot-messages worden
   * eerst ~800ms vervangen door een typing-indicator (3 stippen in een
   * grijze bubbel met bot-avatar) voordat ze gerevealed worden. Geeft de
   * indruk dat de bot écht typt i.p.v. instant antwoorden.
   *
   * Per message-id slaan we het reveal-tijdstip op. `0` = nu zichtbaar.
   * Bij rehydratie (refresh) markeren we alle bestaande messages als
   * al-revealed, zodat history geen typing-fase doorloopt.
   */
  private revealAt = new Map<string, number>();
  private revealTimers = new Set<number>();
  /** Hoe lang de bot "typt" voordat een nieuwe message zichtbaar wordt. */
  private static readonly BOT_TYPING_MS = 800;

  /**
   * Author-soort:
   *   customer  – de bezoeker zelf (rechts, blauw)
   *   operator  – echte medewerker (links, grijs, avatar = initials)
   *   assistant – virtuele bot (links, paars, avatar = 🤖)
   *   system    – pill-bericht in het midden ("Gesprek gestart…")
   */

  static override styles = css`
    :host {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 0;
    }

    .list {
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
      overflow-x: hidden;
      padding: 20px 16px 10px;
      display: flex;
      flex-direction: column;
      gap: 2px;
      background: var(--ec-background);
      scrollbar-width: thin;
      scrollbar-color: rgba(0,0,0,.15) transparent;
    }
    .list::-webkit-scrollbar { width: 8px; }
    .list::-webkit-scrollbar-thumb { background: rgba(0,0,0,.15); border-radius: 8px; }

    /* Day divider */
    .day-divider {
      display: flex;
      align-items: center;
      gap: 10px;
      margin: 12px 4px 8px;
      font: 600 11px/1 var(--ec-font-family);
      color: #8C8D8F;
      letter-spacing: .04em;
      text-transform: uppercase;
    }
    .day-divider::before,
    .day-divider::after {
      content: '';
      flex: 1;
      height: 1px;
      background: var(--ec-border);
    }

    /* System message */
    .sys {
      align-self: center;
      background: rgba(25,27,31,0.04);
      color: #5E5F62;
      padding: 6px 12px;
      border-radius: 9999px;
      font: 500 11px/1.4 var(--ec-font-family);
      margin: 8px 0;
      max-width: 85%;
      text-align: center;
    }

    /* Message row */
    .msg-row {
      display: flex;
      gap: 8px;
      margin: 4px 0;
      max-width: 100%;
    }
    .msg-row.user { justify-content: flex-end; }
    .msg-row.agent { justify-content: flex-start; }
    .msg-row.bot { justify-content: flex-start; }

    .av {
      width: 28px;
      height: 28px;
      border-radius: 9999px;
      flex: 0 0 auto;
      display: flex;
      align-items: center;
      justify-content: center;
      font: 700 11px/1 var(--ec-font-family);
      color: #fff;
      background: var(--ec-accent);
      /* Top-anchor: bij single-line zit avatar visueel naast de bubble,
         bij multi-line blijft 'ie naast de eerste regel staan i.p.v. mee
         te zakken naar de onderkant. margin-top compenseert het verschil
         tussen avatar-top (0) en de center-line van de eerste bubble-
         regel (~14px = padding 10 + half line-height) zodat de avatar-
         center tegenover de eerste-regel-center valt. */
      align-self: flex-start;
      margin-top: 6px;
    }
    .av.bot {
      background: var(--ec-assistant-bg);
      color: var(--ec-on-assistant);
      font-size: 14px;
    }
    .av.hidden { visibility: hidden; }

    .group {
      display: flex;
      flex-direction: column;
      max-width: 78%;
      gap: 2px;
    }
    .msg-row.user .group { align-items: flex-end; }

    .bubble {
      padding: 10px 14px;
      border-radius: var(--ec-radius-bubble);
      font: 400 14px/1.45 var(--ec-font-family);
      word-wrap: break-word;
      overflow-wrap: anywhere;
      white-space: pre-wrap;
    }
    .msg-row.agent .bubble {
      background: var(--ec-their-message-bg);
      color: var(--ec-on-their-message);
      border:
        var(--ec-their-message-border-width)
        var(--ec-their-message-border-style)
        var(--ec-their-message-border-color);
      border-bottom-left-radius: var(--ec-radius-bubble-tail);
    }
    .msg-row.bot .bubble {
      background: var(--ec-assistant-message-bg);
      color: var(--ec-on-assistant-message);
      border:
        var(--ec-assistant-message-border-width)
        var(--ec-assistant-message-border-style)
        var(--ec-assistant-message-border-color);
      border-bottom-left-radius: var(--ec-radius-bubble-tail);
    }
    .msg-row.agent .group .bubble + .bubble,
    .msg-row.bot .group .bubble + .bubble {
      border-top-left-radius: var(--ec-radius-bubble-tail);
    }
    .msg-row.agent .group .bubble:not(:last-child),
    .msg-row.bot .group .bubble:not(:last-child) {
      border-bottom-left-radius: var(--ec-radius-bubble);
    }

    .msg-row.user .bubble {
      background: var(--ec-my-message-bg);
      color: var(--ec-on-my-message);
      border:
        var(--ec-my-message-border-width)
        var(--ec-my-message-border-style)
        var(--ec-my-message-border-color);
      border-bottom-right-radius: var(--ec-radius-bubble-tail);
    }
    .msg-row.user .group .bubble + .bubble {
      border-top-right-radius: var(--ec-radius-bubble-tail);
    }
    .msg-row.user .group .bubble:not(:last-child) {
      border-bottom-right-radius: var(--ec-radius-bubble);
    }

    .msg-meta {
      font: 500 10px/1 var(--ec-font-family);
      color: #8C8D8F;
      padding: 4px 6px 0;
      display: flex;
      align-items: center;
      gap: 4px;
    }
    .msg-row.user .msg-meta { justify-content: flex-end; }
    .msg-meta .read { color: var(--ec-accent); }
    .msg-meta .failed { color: var(--ec-error); }

    /* Retry-knop verschijnt naast het ⚠-icoon bij failed messages.
       Tekst-link-stijl i.p.v. button-pill: de meta-row is klein/secondair,
       een pill zou visueel zwaarder zijn dan het bericht zelf. Onderlijning
       maakt de affordance duidelijk; focus-visible krijgt een dikkere
       lijn voor toetsenbord-navigatie. */
    .msg-meta .retry-btn {
      appearance: none;
      background: none;
      border: 0;
      padding: 0;
      margin: 0;
      font: inherit;
      color: var(--ec-error);
      cursor: pointer;
      text-decoration: underline;
      text-underline-offset: 2px;
    }
    .msg-meta .retry-btn:hover {
      text-decoration-thickness: 2px;
    }
    .msg-meta .retry-btn:focus-visible {
      outline: 2px solid var(--ec-error);
      outline-offset: 2px;
      border-radius: 2px;
    }

    /* Typing indicator */
    .typing {
      background: var(--ec-their-message-bg);
      border-radius: var(--ec-radius-bubble);
      border-bottom-left-radius: var(--ec-radius-bubble-tail);
      padding: 12px 14px;
      display: inline-flex;
      gap: 4px;
      align-items: center;
      width: max-content;
    }
    .typing .dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: #8C8D8F;
      opacity: .5;
      animation: typing 1.2s infinite ease-in-out;
    }
    .typing .dot:nth-child(2) { animation-delay: .15s; }
    .typing .dot:nth-child(3) { animation-delay: .3s; }
    @keyframes typing {
      0%, 60%, 100% { transform: translateY(0); opacity: .4; }
      30% { transform: translateY(-3px); opacity: .9; }
    }

    /* File attachments (basis) */
    .file-att {
      display: flex;
      align-items: center;
      gap: 10px;
      background: #fff;
      border: 1px solid var(--ec-border);
      border-radius: 12px;
      padding: 10px 12px;
      color: var(--ec-text);
      text-decoration: none;
      min-width: 220px;
      max-width: 260px;
    }
    .msg-row.user .file-att {
      background: rgba(255,255,255,.15);
      border-color: rgba(255,255,255,.25);
      color: #fff;
    }
    .file-att .ic {
      width: 34px;
      height: 34px;
      border-radius: 8px;
      background: color-mix(in oklab, var(--ec-primary), white 88%);
      color: var(--ec-primary);
      display: flex;
      align-items: center;
      justify-content: center;
      flex: 0 0 auto;
    }
    .msg-row.user .file-att .ic {
      background: rgba(255,255,255,.2);
      color: #fff;
    }
    .file-att .info { min-width: 0; flex: 1; }
    .file-att .fn { font: 600 13px/1.25 var(--ec-font-family); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .file-att .fs { font: 500 11px/1.3 var(--ec-font-family); opacity: .7; margin-top: 2px; }

    .img-att {
      max-width: 260px;
      border-radius: 14px;
      overflow: hidden;
      border: 1px solid var(--ec-border);
      background: #E8EAF1;
      aspect-ratio: 4/3;
      position: relative;
    }
    .img-att .ph {
      position: absolute;
      inset: 0;
      background:
        linear-gradient(135deg, rgba(12,28,71,.05), rgba(12,28,71,.15)),
        repeating-linear-gradient(45deg, #D6DAE5 0 10px, #CED3E1 10px 20px);
      display: flex;
      align-items: center;
      justify-content: center;
      color: #5E6B85;
      font: 500 12px/1 var(--ec-font-family);
      letter-spacing: .04em;
      text-transform: uppercase;
    }
    /* Preview-variant: echte foto met data-URI als <img>. Maakt de
       container klikbaar zodat tap = open-in-new-tab (full-size).
       Width: 100% van de bovenliggende .group (die zelf max-width
       op 78% heeft), tot het max-width-plafond van 260px. Geen vaste
       aspect-ratio op het anchor zodat hele dunne of brede foto's
       hun eigen verhouding kunnen behouden. */
    .img-att--preview {
      display: block;
      width: 100%;
      aspect-ratio: auto;            /* override .img-att's 4/3 */
      background: transparent;       /* override .img-att's placeholder-bg */
      border: 1px solid var(--ec-border);
      text-decoration: none;
      cursor: zoom-in;
      transition: transform var(--ec-duration-fast) var(--ec-ease-smooth);
    }
    .img-att--preview:hover {
      transform: scale(1.01);
    }
    .img-att--preview img {
      display: block;
      width: 100%;
      height: auto;
      max-height: 220px;
      object-fit: contain;
    }

    /* AI-gebruiksvoorwaarden — inklapbaar blok bovenaan de AAVA-stream. */
    .ai-terms {
      margin: 0 0 12px;
      border: 1px solid var(--ec-border);
      border-radius: var(--ec-radius-card);
      background: var(--ec-surface-sunken);
    }
    .ai-terms > summary {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 14px;
      cursor: pointer;
      list-style: none;
      font: 600 12px/1.3 var(--ec-font-family);
      color: var(--ec-text);
      user-select: none;
    }
    .ai-terms > summary::-webkit-details-marker { display: none; }
    /* Disclosure-chevron rechts; draait bij open. */
    .ai-terms > summary::after {
      content: '';
      margin-left: auto;
      width: 8px;
      height: 8px;
      border-right: 2px solid var(--ec-text-soft);
      border-bottom: 2px solid var(--ec-text-soft);
      transform: rotate(45deg);
      transition: transform var(--ec-duration-fast) var(--ec-ease-smooth);
    }
    .ai-terms[open] > summary::after { transform: rotate(-135deg); }
    .ai-terms > summary:focus-visible {
      outline: 2px solid var(--ec-accent);
      outline-offset: 2px;
      border-radius: var(--ec-radius-card);
    }
    .ai-terms-body {
      padding: 0 14px 12px;
      max-height: 260px;
      overflow-y: auto;
      font: 400 12px/1.5 var(--ec-font-family);
      color: var(--ec-text-muted);
    }
    .ai-terms-body p { margin: 0 0 6px; }
    .ai-terms-body p:last-child { margin-bottom: 0; }
    .ai-terms-body strong { color: var(--ec-text); font-weight: 700; }
    /* Genummerde sectiekop krijgt extra ruimte erboven. */
    .ai-terms-body .terms-section { margin-top: 12px; }

    /* Inline contract-choices — bot-row met avatar + cards in een vertical
       stack. Layout consistent met message-bubble-padding zodat ze in de
       chat-stream passen. */
    .choice-group {
      max-width: min(86%, 380px);
      gap: 6px;
    }
    .choices {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }
    .choice-card {
      width: 100%;
      text-align: left;
      appearance: none;
      background: var(--ec-their-message-bg);
      border: 1px solid transparent;
      border-radius: var(--ec-radius-bubble);
      border-bottom-left-radius: var(--ec-radius-bubble-tail);
      padding: 10px 14px;
      cursor: pointer;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      color: var(--ec-on-their-message);
      transition:
        border-color var(--ec-duration-fast) var(--ec-ease-smooth),
        background var(--ec-duration-fast) var(--ec-ease-smooth);
    }
    .choice-card:hover {
      border-color: var(--ec-accent);
      background: color-mix(in oklab, var(--ec-accent) 6%, var(--ec-their-message-bg));
    }
    .choice-card:hover .choice-cue {
      transform: translateX(2px);
    }
    .choice-card:focus-visible {
      outline: 2px solid var(--ec-accent);
      outline-offset: 2px;
    }
    .choice-address {
      font: 700 13px/1.3 var(--ec-font-family);
    }
    /* Klik-affordance: "Kies ›" in de accent-kleur, rechts uitgelijnd. */
    .choice-cue {
      display: inline-flex;
      align-items: center;
      gap: 3px;
      flex-shrink: 0;
      color: var(--ec-accent);
      font: 600 11px/1 var(--ec-font-family);
      transition: transform var(--ec-duration-fast) var(--ec-ease-smooth);
    }
    .choice-skeleton {
      height: 52px;
      border-radius: var(--ec-radius-bubble);
      background: linear-gradient(
        90deg,
        var(--ec-their-message-bg) 0%,
        color-mix(in oklab, var(--ec-their-message-bg) 80%, white) 50%,
        var(--ec-their-message-bg) 100%
      );
      background-size: 200% 100%;
      animation: choice-shimmer 1.4s linear infinite;
    }
    @keyframes choice-shimmer {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }
    .choice-loading {
      font: 500 11px/1.4 var(--ec-font-family);
      color: #8c8d8f;
      padding: 2px 4px;
    }
    .choice-empty,
    .choice-error {
      padding: 10px 14px;
      border-radius: var(--ec-radius-bubble);
      background: var(--ec-their-message-bg);
      color: var(--ec-on-their-message);
      font: 500 13px/1.45 var(--ec-font-family);
    }
    .choice-retry {
      display: inline-block;
      margin-top: 6px;
      appearance: none;
      border: 0;
      background: var(--ec-accent);
      color: var(--ec-on-accent, #fff);
      font: 700 11px/1 var(--ec-font-family);
      padding: 6px 12px;
      border-radius: 9999px;
      cursor: pointer;
    }

    /* YouTube-card — thumbnail + play-overlay + meta. Veilig pad zonder
       iframe-embed: clickbare link opent youtube.com in nieuw tabblad.
       Layout-consistentie met andere bot-bubbles via dezelfde max-width
       en radius-tokens. */
    .yt-card {
      display: flex;
      flex-direction: column;
      width: 100%;
      max-width: 280px;
      text-decoration: none;
      color: inherit;
      background: var(--ec-their-message-bg);
      border-radius: var(--ec-radius-bubble);
      border-bottom-left-radius: var(--ec-radius-bubble-tail);
      overflow: hidden;
      cursor: pointer;
      transition: transform var(--ec-duration-fast) var(--ec-ease-smooth),
                  box-shadow var(--ec-duration-fast) var(--ec-ease-smooth);
    }
    .yt-card:hover,
    .yt-card:focus-visible {
      transform: translateY(-1px);
      box-shadow: 0 6px 16px -8px rgba(0, 0, 0, 0.25);
      outline: none;
    }
    .yt-card:focus-visible {
      box-shadow: 0 0 0 2px var(--ec-accent), 0 6px 16px -8px rgba(0, 0, 0, 0.25);
    }
    .yt-thumb {
      position: relative;
      display: block;
      aspect-ratio: 16 / 9;
      background: #1f1f1f;
      overflow: hidden;
    }
    .yt-thumb img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
    .yt-play {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      pointer-events: none;
    }
    .yt-meta {
      display: flex;
      flex-direction: column;
      gap: 2px;
      padding: 8px 12px 10px;
      color: var(--ec-on-their-message);
    }
    .yt-title {
      font: 600 13px/1.3 var(--ec-font-family);
    }
    .yt-domain {
      font: 500 11px/1 var(--ec-font-family);
      opacity: 0.6;
    }

    /* Rating-prompt — bot-balloon-stijl met sterren-form erin. Avatar
       links, card daarnaast, meta-regel ("Digitale assistent · NU")
       eronder. Past visueel in de chat-thread zodat de vraag voelt als
       een opvolgende boodschap van de bot. */
    .msg-row.rating-row .group {
      max-width: min(86%, 380px);
    }
    .msg-row.rating-row embrace-rating-card {
      display: block;
    }

    /* Self-fix ja/nee-vraag — zelfde bot-balloon-behandeling als de
       rating-prompt hierboven. */
    .msg-row.self-fix-row .group {
      max-width: min(86%, 380px);
    }
    .msg-row.self-fix-row embrace-self-fix-prompt {
      display: block;
    }
  `;

  override willUpdate(changed: Map<string, unknown>): void {
    if (!changed.has('assistantMessages')) return;
    // Bij de eerste render (mount of refresh) zijn ALLE assistant-messages
    // "geschiedenis" — de bezoeker zag ze al vóór de refresh. Direct
    // revealen zonder typing-effect, anders krijgt de bezoeker een
    // onrustige reveal-cascade en bijbehorende scroll-shifts terwijl er
    // niets nieuws is gebeurd. `hasUpdated` is Lit's eigen first-render-
    // flag: false in deze willUpdate vóór de eerste update-cyclus,
    // daarna true.
    const isFirstRender = !this.hasUpdated;
    const now = Date.now();
    for (const m of this.assistantMessages) {
      if (this.revealAt.has(m.id)) continue;
      if (isFirstRender) {
        this.revealAt.set(m.id, 0);
        continue;
      }
      if (m.author === 'assistant') {
        // Korte typing-show vóór reveal — alleen voor bot-messages, en
        // alleen voor messages die écht nieuw zijn (nieuwe bot-respons
        // tijdens deze sessie, niet rehydratie).
        const at = now + EmbraceMessageList.BOT_TYPING_MS;
        this.revealAt.set(m.id, at);
        const handle = window.setTimeout(() => {
          this.revealTimers.delete(handle);
          this.requestUpdate();
        }, EmbraceMessageList.BOT_TYPING_MS);
        this.revealTimers.add(handle);
      } else {
        // Customer-messages (of andere) zijn direct zichtbaar.
        this.revealAt.set(m.id, 0);
      }
    }
    // Houd de map gesynchroniseerd met de huidige messages — voorkomt
    // onbegrensde groei in lange sessies (SESSION_RESET wist messages
    // maar de component blijft gemount).
    if (this.revealAt.size > this.assistantMessages.length) {
      const live = new Set(this.assistantMessages.map((m) => m.id));
      for (const k of this.revealAt.keys()) {
        if (!live.has(k)) this.revealAt.delete(k);
      }
    }
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    for (const t of this.revealTimers) clearTimeout(t);
    this.revealTimers.clear();
    this.revealAt.clear();
    this.segmentCache.clear();
  }

  override render() {
    const { rows, botIsTyping } = this.buildRows();

    return html`
      <div
        class="list"
        part="message-list"
        role="log"
        aria-live="polite"
      >
        ${this.aavaDisclaimer ? this.renderAavaDisclaimer() : nothing}
        ${rows.length === 0 && !this.agentTyping && !botIsTyping && !this.aavaThinking ? nothing : rows}
        ${this.agentTyping ? this.renderTyping() : nothing}
        ${botIsTyping || this.aavaThinking ? this.renderBotTyping() : nothing}
        ${this.sessionEnded ? this.renderEndedBadge() : nothing}
        ${this.ratingVariant !== 'hidden'
          ? this.renderRatingRow()
          : nothing}
        ${this.showContractChoices && this.contracts
          ? this.renderContractChoices(this.contracts)
          : nothing}
        ${this.showSelfFix ? this.renderSelfFixRow() : nothing}
      </div>
    `;
  }

  /**
   * Self-fix ja/nee-vraag als bot-balloon. Spiegelt `renderRatingRow` —
   * avatar links, kaartje ernaast, meta-regel eronder — zodat de vraag
   * visueel deel uitmaakt van de chat-stream.
   */
  private renderSelfFixRow() {
    return html`
      <div class="msg-row bot self-fix-row">
        <span class="av bot" aria-hidden="true">🤖</span>
        <div class="group">
          <embrace-self-fix-prompt></embrace-self-fix-prompt>
          <div class="msg-meta">
            <span>${i18n.messages.botLabel}</span>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Rating-prompt als bot-balloon. Hergebruikt `<embrace-rating-card>`
   * voor de interactieve content (sterren + buttons of success-state)
   * en wikkelt 'm in een `.msg-row.bot`-container met avatar + meta-
   * regel zodat hij visueel deel uitmaakt van de chat-stream.
   *
   * Avatar = 🤖 (consistent met andere bot-pills). Meta-regel toont
   * "Digitale assistent · {tijd}" zoals bij andere bot-balloons.
   */
  private renderRatingRow() {
    return html`
      <div class="msg-row bot rating-row">
        <span class="av bot" aria-hidden="true">🤖</span>
        <div class="group">
          <embrace-rating-card
            variant=${this.ratingVariant === 'idle' ? 'idle' : 'submitted'}
            .submittedRating=${this.submittedRating}
          ></embrace-rating-card>
          <div class="msg-meta">
            <span>${i18n.messages.botLabel}</span>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Combineer server + pending messages + bouw day-dividers op.
   * Gebruik author-runs voor avatar-grouping (avatar alleen bij 1e row per run).
   *
   * Geeft naast de rows ook `botIsTyping` terug — afgeleid uit dezelfde
   * pass over assistantMessages — zodat render() niet een tweede keer
   * over dezelfde lijst hoeft te lopen.
   */
  private buildRows() {
    type CombinedAuthor = RowAuthor | 'system';
    const combined: Array<{
      id: string;
      author: CombinedAuthor;
      text: string;
      timestamp: number;
      files?: AttachedFile[];
      status?: MessageStatus;
    }> = [];

    const now = Date.now();
    let botIsTyping = false;
    for (const a of this.assistantMessages) {
      // Typing-effect: bot-messages worden pas onthuld nadat hun
      // reveal-tijdstip is bereikt. Tot die tijd skippen we ze hier en
      // toont render() in hun plaats een typing-indicator.
      const revealAt = this.revealAt.get(a.id) ?? 0;
      if (revealAt > now) {
        if (a.author === 'assistant') botIsTyping = true;
        continue;
      }
      combined.push({
        id: a.id,
        author: a.author,
        text: a.text,
        timestamp: a.timestamp,
        files: a.files,
        // assistant-flow customer-replies krijgen geen read-receipt: ze gaan
        // niet via de server. Behandel ze als 'awaiting' zodat de status-icon
        // klopt zonder dubbel-vinkje.
        status: a.author === 'customer' ? 'awaiting' : undefined,
      });
    }
    for (const m of this.serverMessages) {
      combined.push({
        id: m.id,
        author: m.author,
        text: m.text,
        timestamp: m.timestamp,
        files: m.files,
        status: m.author === 'customer' ? 'sent' : undefined,
      });
    }
    for (const p of this.pendingMessages) {
      combined.push({
        id: p.localId,
        author: 'customer',
        text: p.text,
        timestamp: p.timestamp,
        files: p.files,
        status: p.status,
      });
    }

    // Sort by timestamp
    combined.sort((a, b) => a.timestamp - b.timestamp);

    const rows: Row[] = [];
    let prevDay: string | null = null;
    let prevAuthor: string | null = null;

    for (let i = 0; i < combined.length; i++) {
      const m = combined[i]!;
      const day = formatDay(m.timestamp);
      if (day !== prevDay) {
        rows.push({ kind: 'day', id: `day-${day}-${i}`, label: day });
        prevDay = day;
        prevAuthor = null;
      }
      if (m.author === 'system') {
        rows.push({ kind: 'system', id: m.id, text: m.text, timestamp: m.timestamp });
        prevAuthor = null;
        continue;
      }
      const showAvatar = m.author !== prevAuthor;
      rows.push({
        kind: 'msg',
        id: m.id,
        author: m.author,
        text: m.text,
        timestamp: m.timestamp,
        files: m.files,
        status: m.status,
        showAvatar,
      });
      prevAuthor = m.author;
    }

    return {
      rows: rows.map((row) => this.renderRow(row)),
      botIsTyping,
    };
  }

  private renderRow(row: Row) {
    if (row.kind === 'day') {
      return html`<div class="day-divider">${row.label}</div>`;
    }
    if (row.kind === 'system') {
      return html`<div class="sys">${row.text}</div>`;
    }
    // row.kind === 'msg' — TypeScript narrowt automatisch.
    const rowClass =
      row.author === 'customer'
        ? 'user'
        : row.author === 'assistant'
          ? 'bot'
          : 'agent';
    const isOperator = row.author === 'operator';
    const isAssistant = row.author === 'assistant';
    const op = this.operator;

    return html`
      <div class=${`msg-row ${rowClass}`}>
        ${isOperator
          ? row.showAvatar
            ? html`<span
                class="av"
                style=${`background: ${op?.avatarColor || 'var(--ec-accent)'};`}
              >${op?.initials ?? 'SJ'}</span>`
            : html`<span class="av hidden">·</span>`
          : nothing}
        ${isAssistant
          ? row.showAvatar
            ? html`<span class="av bot" aria-hidden="true">🤖</span>`
            : html`<span class="av hidden">·</span>`
          : nothing}
        <div class="group">
          ${row.text
            ? isAssistant
              ? this.renderAssistantContent(row.text)
              : html`<div
                  class="bubble"
                  part=${row.author === 'customer' ? 'message-mine' : 'message-theirs'}
                >${row.text}</div>`
            : nothing}
          ${(row.files ?? []).map((f) => this.renderFile(f, row.author === 'customer'))}
          <div class="msg-meta">
            ${isOperator && row.showAvatar && op
              ? html`<span>${op.name} · </span>`
              : nothing}
            ${isAssistant && row.showAvatar
              ? html`<span>${i18n.messages.botLabel} · </span>`
              : nothing}
            <span>${formatTime(row.timestamp)}</span>
            ${row.author === 'customer' ? this.renderStatus(row.status) : nothing}
            ${row.author === 'customer' && row.status === 'failed'
              ? this.renderRetryButton(row.id)
              : nothing}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Retry-knop voor mislukt-verzonden customer-messages. Klik dispatcht
   * een `retry-message`-event met de pending-message localId; de host
   * vertaalt dat naar `MESSAGE_RETRY_REQUESTED` op de controller, die
   * de status weer op 'sending' zet en een nieuwe POST doet. Knop
   * verdwijnt automatisch zodra status !== 'failed', dus dubbel-klik
   * kan niet twee parallel-requests veroorzaken.
   */
  private renderRetryButton(localId: string) {
    return html`<button
      type="button"
      class="retry-btn"
      aria-label=${i18n.messages.btnRetryAria}
      @click=${() => this.dispatchRetry(localId)}
    >${i18n.messages.btnRetry}</button>`;
  }

  private dispatchRetry(localId: string): void {
    this.dispatchEvent(
      new CustomEvent('retry-message', {
        detail: { localId },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private renderStatus(status: MessageStatus | undefined) {
    if (!status || status === 'sent') {
      return html`<span class="read" title=${i18n.messages.statusGelezen}>${iconCheckDouble()}</span>`;
    }
    if (status === 'sending') return html`<span title=${i18n.messages.statusVerzenden}>⏱</span>`;
    if (status === 'awaiting') {
      return html`<span title=${i18n.messages.statusVerzonden}>${iconCheck()}</span>`;
    }
    if (status === 'failed') {
      return html`<span class="failed" title=${i18n.messages.statusFailed}>⚠</span>`;
    }
    return nothing;
  }

  private renderFile(file: AttachedFile, _isUser: boolean) {
    if (file.kind === 'image') {
      // Met `dataUri` (AVAA-flow vlak-na-verzending): toon de echte
      // image als preview-thumb + click-to-open-in-new-tab. Zonder
      // dataUri (classic chat-server flow, of na lange tijd in
      // history): fallback naar de pre-existing placeholder met
      // alleen filename + grootte.
      if (file.dataUri) {
        return html`
          <a
            class="img-att img-att--preview"
            href=${file.dataUri}
            target="_blank"
            rel="noreferrer noopener"
            title=${file.name}
            aria-label=${`${file.name} — open in nieuw tabblad`}
          >
            <img
              src=${file.dataUri}
              alt=${file.name}
              loading="lazy"
              referrerpolicy="no-referrer"
            />
          </a>
        `;
      }
      return html`
        <div class="img-att" title=${file.name}>
          <div class="ph">Foto · ${file.sizeLabel}</div>
        </div>
      `;
    }
    return html`
      <a class="file-att" href="#" @click=${(e: Event) => e.preventDefault()}>
        <span class="ic">${iconAttach()}</span>
        <span class="info">
          <span class="fn">${file.name}</span>
          <span class="fs">${file.sizeLabel}</span>
        </span>
      </a>
    `;
  }

  private renderTyping() {
    const op = this.operator;
    return html`
      <div class="msg-row agent">
        <span
          class="av"
          style=${`background: ${op?.avatarColor || 'var(--ec-accent)'};`}
        >${op?.initials ?? 'SJ'}</span>
        <div class="group">
          <div class="typing" part="typing-indicator">
            <span class="dot"></span>
            <span class="dot"></span>
            <span class="dot"></span>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Typing-indicator voor de digitale assistent. Zelfde 3-stippen-bubbel
   * als `renderTyping()` maar met de bot-avatar (🤖) zodat de bezoeker
   * direct ziet dat de bot "denkt", niet de medewerker.
   */
  private renderBotTyping() {
    return html`
      <div class="msg-row bot">
        <span class="av bot" aria-hidden="true">🤖</span>
        <div class="group">
          <div class="typing" part="typing-indicator">
            <span class="dot"></span>
            <span class="dot"></span>
            <span class="dot"></span>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Inline contract-choice-block: rendert als bot-row (avatar + groep)
   * met daaronder de keuze-knoppen. Visueel een directe voortzetting
   * van de "Voor welke woning?"-bot-message hiervoor.
   *
   *   - loading/idle → skeleton-rijen
   *   - error        → korte fout-melding + retry-knop
   *   - empty        → "geen contracten" tekst
   *   - loaded       → een lijst van full-width keuze-knoppen
   *
   * Klik op een keuze dispatcht `contract-select` (zelfde event als
   * de aside-component verzond). Geen radio-state — eenmalige keuze.
   */
  /**
   * Inklapbaar gebruiksvoorwaarden-blok bovenaan de AAVA-chat. Native
   * `<details>`/`<summary>` → toetsenbord-toegankelijk en schermlezer-
   * vriendelijk zonder extra ARIA. Standaard dichtgeklapt; de bezoeker
   * opent het om de volledige tekst te lezen. `white-space: pre-line`
   * behoudt de paragraaf-structuur uit de i18n-tekst.
   */
  private renderAavaDisclaimer() {
    const lines = this.aavaDisclaimer.split('\n');
    return html`
      <details class="ai-terms" part="ai-terms">
        <summary>
          ${iconInfo()}
          <span>${i18n.aava.disclaimerLongTitle}</span>
        </summary>
        <div class="ai-terms-body">
          ${lines.map((l) => this.renderTermsLine(l))}
        </div>
      </details>
    `;
  }

  /**
   * Eén regel van de gebruiksvoorwaarden. Genummerde sectiekoppen ("1. …")
   * worden volledig vet; "Label: tekst"-regels krijgen een vet lead-in label.
   * Lege regels (paragraaf-scheiders) worden overgeslagen — de p-marges
   * verzorgen de witruimte.
   */
  private renderTermsLine(line: string) {
    const text = line.trim();
    if (text === '') return nothing;
    if (/^\d+\.\s/.test(text)) {
      return html`<p class="terms-section"><strong>${text}</strong></p>`;
    }
    const label = text.match(/^([^:]{2,60}):\s(.*)$/);
    if (label) {
      return html`<p><strong>${label[1]}:</strong> ${label[2]}</p>`;
    }
    return html`<p>${text}</p>`;
  }

  private renderContractChoices(c: ContractsSlice) {
    const copy = i18n.contracts;
    let inner;
    if (c.status === 'idle' || c.status === 'loading') {
      inner = html`
        <div class="choice-skeleton" aria-hidden="true"></div>
        <div class="choice-skeleton" aria-hidden="true"></div>
        <span class="choice-loading" role="status">${copy.loading}</span>
      `;
    } else if (c.status === 'error') {
      inner = html`
        <div class="choice-error" role="alert">
          ${this.contractsErrorCopy(c.error)}
          <button
            class="choice-retry"
            type="button"
            @click=${this.onContractsRetry}
          >${copy.btnRetry}</button>
        </div>
      `;
    } else if (c.items.length === 0) {
      inner = html`<div class="choice-empty">${copy.empty}</div>`;
    } else {
      inner = html`
        <div class="choices" role="group" aria-label=${copy.title}>
          ${c.items.map((item) => this.renderContractChoice(item))}
        </div>
      `;
    }
    return html`
      <div class="msg-row bot">
        <span class="av bot" aria-hidden="true">🤖</span>
        <div class="group choice-group">${inner}</div>
      </div>
    `;
  }

  private renderContractChoice(c: ContractDetailed) {
    // Alleen het adres tonen — geen periode of status/achterstand-badges
    // (SPEC-CRM-163423 item 2b; de proxy stript die data sowieso al weg).
    // De "Kies ›"-cue + aria-label maken duidelijk dat de kaart klikbaar is.
    const address = formatUnitAddress(c);
    return html`
      <button
        class="choice-card"
        type="button"
        aria-label=${i18n.contracts.chooseAria(address)}
        @click=${() => this.onContractSelect(c.id)}
      >
        <span class="choice-address">${address}</span>
        <span class="choice-cue" aria-hidden="true">
          ${i18n.contracts.chooseCue}
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none"
            stroke="currentColor" stroke-width="2.5" stroke-linecap="round"
            stroke-linejoin="round" aria-hidden="true">
            <polyline points="9 6 15 12 9 18"></polyline>
          </svg>
        </span>
      </button>
    `;
  }

  private contractsErrorCopy(reason: ContractsSlice['error']): string {
    const copy = i18n.contracts;
    switch (reason) {
      case 'unauthorized':
        return copy.errorUnauthorized;
      case 'unavailable':
        return copy.errorUnavailable;
      case 'parse':
        return copy.errorParse;
      case 'network':
      default:
        return copy.errorNetwork;
    }
  }

  private onContractSelect(contractId: string): void {
    this.dispatchEvent(
      new CustomEvent('contract-select', {
        detail: { contractId },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private onContractsRetry(): void {
    this.dispatchEvent(
      new CustomEvent('contract-retry', { bubbles: true, composed: true }),
    );
  }

  /**
   * Cache van segment-arrays per assistant-tekst. Lit roept render()
   * vaak aan (elke state-change, elke prop-update), maar message-tekst
   * is immutable. Door op de string-identity te keyen vermijden we
   * dat de regex-parser bij elke render opnieuw door elke bestaande
   * AAVA-reply walkt — dat is O(messages × renders) zonder cache.
   */
  private readonly segmentCache = new Map<string, ReturnType<typeof parseAavaMessage>>();

  /**
   * Assistant-tekst kan structurele media-elementen bevatten (vooral
   * YouTube-iframes uit AAVA-replies). We splitsen de tekst in
   * segments en renderen elk segment apart: tekst-segments als
   * gewone chat-bubble, video-segments als clickable card met
   * thumbnail. Het YouTube-iframe wordt NIET geëmbed — alleen een
   * <a target="_blank"> naar youtube.com/watch (XSS-veilig).
   */
  private renderAssistantContent(text: string) {
    let segments = this.segmentCache.get(text);
    if (!segments) {
      segments = parseAavaMessage(text);
      this.segmentCache.set(text, segments);
    }
    if (segments.length === 0) return nothing;
    return segments.map((seg) =>
      seg.kind === 'text'
        ? html`<div class="bubble" part="message-theirs">${seg.text}</div>`
        : this.renderYoutubeCard(seg.videoId, seg.title),
    );
  }

  /**
   * YouTube-thumbnail-card. Geen iframe — alleen een veilige link met
   * thumbnail. `rel="noreferrer"` voorkomt referrer-lekkage; `target="_blank"`
   * houdt de chat actief in het huidige tabblad terwijl YouTube opent in
   * een nieuw venster.
   */
  private renderYoutubeCard(videoId: string, title: string) {
    return html`
      <a
        class="yt-card"
        href=${youtubeWatchUrl(videoId)}
        target="_blank"
        rel="noreferrer noopener"
        aria-label=${`Open ${title} op YouTube`}
      >
        <span class="yt-thumb">
          <img
            src=${youtubeThumbnailUrl(videoId)}
            alt=""
            loading="lazy"
            referrerpolicy="no-referrer"
          />
          <span class="yt-play" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="36" height="36">
              <circle cx="12" cy="12" r="12" fill="rgba(0,0,0,0.7)" />
              <path d="M10 8 L16 12 L10 16 Z" fill="#fff" />
            </svg>
          </span>
        </span>
        <span class="yt-meta">
          <span class="yt-title">${title}</span>
          <span class="yt-domain">youtube.com</span>
        </span>
      </a>
    `;
  }

  private renderEndedBadge() {
    const ts = this.sessionEndedAt
      ? formatTime(this.sessionEndedAt)
      : formatTime(Date.now());
    const op = this.operator;
    const by =
      this.sessionEndedReason === 'customer'
        ? i18n.messages.endedByJou
        : op
          ? op.name.split(' ')[0]
          : null;
    return html`
      <div class="sys" style="margin-top: 4px;">
        ${i18n.messages.endedAt(ts, by ?? null)}
      </div>
    `;
  }

}

/* =======================
 * Helpers
 * ======================= */

function formatTime(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
}

function formatDay(ts: number): string {
  const d = new Date(ts);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const isSameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  if (isSameDay(d, today)) {
    return `${i18n.messages.today} · ${d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long' })}`;
  }
  if (isSameDay(d, yesterday)) {
    return `${i18n.messages.yesterday} · ${d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long' })}`;
  }
  return d.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' });
}

declare global {
  interface HTMLElementTagNameMap {
    'embrace-message-list': EmbraceMessageList;
  }
}
